/**
 * Rendu du document riche d'une leçon (docs/11 §3).
 *
 * **Aucun `dangerouslySetInnerHTML` ici, et il ne doit jamais y en avoir.**
 * C'est tout l'intérêt de stocker un document structuré plutôt que du HTML :
 * chaque nœud typé est mappé vers un composant React, et React échappe le
 * texte. Un contenu hostile stocké en base ne peut donc pas s'exécuter — même
 * si la validation serveur laissait passer quelque chose, ce rendu n'a aucun
 * chemin pour l'interpréter.
 *
 * Un nœud inconnu est **ignoré silencieusement** ici, contrairement au serveur
 * qui le refuse à l'écriture. L'asymétrie est voulue : le serveur est le garde,
 * et c'est là qu'il faut échouer bruyamment ; l'affichage, lui, doit rester
 * lisible face à un document écrit par une version plus récente de l'éditeur
 * plutôt que de casser toute la page pour un bloc.
 */
import type { ReactNode } from "react";

import type { ContentDocument, ContentNode } from "@/shared/richtext/document";

/** Marques autorisées → rendu. Toute autre est ignorée. */
function withMarks(text: string, marks: ContentNode["marks"]): ReactNode {
  return (marks ?? []).reduce<ReactNode>((rendered, mark) => {
    if (mark.type === "bold") return <strong>{rendered}</strong>;
    if (mark.type === "italic") return <em>{rendered}</em>;
    if (mark.type === "underline") return <u>{rendered}</u>;
    if (mark.type === "strike") return <s>{rendered}</s>;
    if (mark.type === "code") {
      return (
        <code className="rounded bg-slate-100 px-1 py-0.5 text-sm dark:bg-slate-800">
          {rendered}
        </code>
      );
    }
    if (mark.type === "link" && typeof mark.attrs?.["href"] === "string") {
      return (
        // `rel` et `target` sont posés ici ET normalisés par le serveur. Les
        // deux, parce qu'un document écrit avant cette règle existe peut-être
        // déjà : se fier au seul stockage ferait dépendre une protection de la
        // date d'écriture de la donnée.
        <a
          href={mark.attrs["href"]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-600 hover:text-brand-700 underline underline-offset-2"
        >
          {rendered}
        </a>
      );
    }
    return rendered;
  }, text);
}

/**
 * L'alignement d'un bloc, rendu en classe et non en `style`.
 *
 * Les quatre valeurs sont bornées par le serveur (`ALLOWED_TEXT_ALIGN`) ; les
 * traduire par une table plutôt que par un `style={{ textAlign: ... }}` fait que
 * même un document écrit avant cette règle — ou par une version plus récente de
 * l'éditeur — ne peut pas injecter de déclaration CSS.
 */
const ALIGNMENT: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
  justify: "text-justify",
};

function alignmentOf(node: ContentNode): string {
  const value = node.attrs?.["textAlign"];
  return typeof value === "string" ? (ALIGNMENT[value] ?? "") : "";
}

/**
 * Les fusions d'une cellule, sous la forme que React attend.
 *
 * Lu défensivement comme l'alignement : les deux valeurs sont bornées par le
 * serveur (`MAX_TABLE_SPAN`), mais ce rendu est le dernier rempart d'un
 * document écrit avant cette règle. Une valeur qui n'est pas un entier positif
 * vaut donc « pas de fusion » plutôt que d'atteindre l'attribut d'une balise.
 */
function spansOf(node: ContentNode): { colSpan?: number; rowSpan?: number } {
  const read = (name: string): number | undefined => {
    const value = node.attrs?.[name];
    return typeof value === "number" && Number.isInteger(value) && value > 1 ? value : undefined;
  };
  const colSpan = read("colspan");
  const rowSpan = read("rowspan");
  return {
    ...(colSpan === undefined ? {} : { colSpan }),
    ...(rowSpan === undefined ? {} : { rowSpan }),
  };
}

/**
 * Bordures et espacement d'une cellule, communs à `th` et `td`.
 *
 * Les marges du premier et du dernier paragraphe sont annulées : un paragraphe
 * porte `my-3`, et une cellule d'une ligne se retrouverait deux fois plus haute
 * que son texte. Seules celles du bord tombent — deux paragraphes dans une même
 * cellule restent séparés.
 */
const CELL =
  "border border-slate-300 px-3 py-2 align-top dark:border-slate-700 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0";

function renderChildren(nodes: ContentNode[] | undefined): ReactNode {
  return (nodes ?? []).map((node, index) => <RichNode key={index} node={node} />);
}

/**
 * Ce qu'une cellule d'en-tête intitule : sa colonne, ou sa ligne.
 *
 * Un `th` sans `scope` laisse le lecteur d'écran deviner (WCAG H63), et il
 * devine mal dès qu'un tableau a des en-têtes ailleurs qu'en première ligne.
 * La réponse se lit dans la **ligne** : une ligne entièrement faite d'en-têtes
 * intitule des colonnes, un en-tête seul au milieu de cellules intitule sa
 * ligne. C'est donc `tableRow` qui la calcule et la passe à ses enfants — la
 * cellule, seule, ne peut pas la connaître.
 */
type HeaderScope = "col" | "row";

function RichNode({ node, scope }: { node: ContentNode; scope?: HeaderScope }): ReactNode {
  switch (node.type) {
    case "text":
      return withMarks(node.text ?? "", node.marks);
    case "paragraph":
      return (
        <p className={`my-3 leading-relaxed ${alignmentOf(node)}`}>
          {renderChildren(node.content)}
        </p>
      );
    case "heading": {
      const level = typeof node.attrs?.["level"] === "number" ? node.attrs["level"] : 2;
      const classes = `mt-6 mb-2 font-semibold text-slate-900 dark:text-slate-100 ${alignmentOf(node)}`;
      // Les niveaux sont bornés à 2–4 par le serveur ; l'`switch` explicite
      // évite un `h${level}` dynamique, que TypeScript ne saurait pas typer et
      // qui accepterait n'importe quelle balise si la borne sautait un jour.
      if (level === 3)
        return <h3 className={`${classes} text-lg`}>{renderChildren(node.content)}</h3>;
      if (level === 4)
        return <h4 className={`${classes} text-base`}>{renderChildren(node.content)}</h4>;
      return <h2 className={`${classes} text-xl`}>{renderChildren(node.content)}</h2>;
    }
    case "bulletList":
      return <ul className="my-3 list-disc space-y-1 pl-6">{renderChildren(node.content)}</ul>;
    case "orderedList":
      return <ol className="my-3 list-decimal space-y-1 pl-6">{renderChildren(node.content)}</ol>;
    case "listItem":
      return <li>{renderChildren(node.content)}</li>;
    case "blockquote":
      return (
        <blockquote className="my-4 border-l-4 border-slate-300 pl-4 text-slate-600 italic dark:border-slate-700 dark:text-slate-400">
          {renderChildren(node.content)}
        </blockquote>
      );
    case "codeBlock":
      return (
        <pre className="my-4 overflow-x-auto rounded-md bg-slate-900 p-4 text-sm text-slate-100">
          <code>{renderChildren(node.content)}</code>
        </pre>
      );
    case "table":
      // Le `div` qui défile est ce qui empêche un tableau plus large que la
      // colonne de texte de pousser toute la page en largeur — sur un écran de
      // téléphone, un tableau de quatre colonnes le fait sinon toujours.
      return (
        <div className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>{renderChildren(node.content)}</tbody>
          </table>
        </div>
      );
    case "tableRow": {
      const cells = node.content ?? [];
      const headerScope: HeaderScope = cells.every((cell) => cell.type === "tableHeader")
        ? "col"
        : "row";
      return (
        <tr>
          {cells.map((cell, index) => (
            <RichNode key={index} node={cell} scope={headerScope} />
          ))}
        </tr>
      );
    }
    case "tableHeader":
      return (
        <th
          {...spansOf(node)}
          scope={scope}
          className={`${CELL} bg-slate-100 text-left font-semibold text-slate-900 dark:bg-slate-800 dark:text-slate-100`}
        >
          {renderChildren(node.content)}
        </th>
      );
    case "tableCell":
      return (
        <td {...spansOf(node)} className={CELL}>
          {renderChildren(node.content)}
        </td>
      );
    case "horizontalRule":
      return <hr className="my-6 border-slate-200 dark:border-slate-800" />;
    case "hardBreak":
      return <br />;
    default:
      return null;
  }
}

export function RichContent({ document }: { document: ContentDocument }) {
  return (
    <div className="text-slate-700 dark:text-slate-300">{renderChildren(document.content)}</div>
  );
}
